import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import "../App.css";
import { collection, addDoc, getDocs, query, orderBy, where, limit, onSnapshot, serverTimestamp, Timestamp} from "firebase/firestore";
import KoalaMath from "../KoalaMath.png";
import KoalaEnglish from "../KoalaEnglish.png";
import KoalaScience from "../KoalaScience.png";
import KoalaHistory from "../KoalaHistory.png";
import { db } from "../firebase";
import defaultProfile from "../UserProfile/EmptyProfile.jpg";
import { FriendsCard } from "./FriendsCard";
import { useFriendManager } from "./friends";
import { usePresence } from "./usePresence";
import { useAutoLogout } from "./useAutoLogout";
import { PrivateChatCard } from "./PrivateChatCard";
import ParentInsightsPanel from "./ParentInsightsPanel";

import {Filter} from "bad-words";
const filter = new Filter();

// English profanity (common words not always included by default, or you want extra assurance)
filter.addWords(
  "sex", "sexual", "porn", "porno", "nude", "nudes", "naked", "hentai", "fetish", "dick", "cock",
  "pussy", "cum", "suck", "fuck", "shit", "bitch", "asshole", "ass", "cunt", "whore", "slut", "boob",
  "fag", "faggot", "retard", "nigger", "nigga", "kike", "chink", "spic", "dyke", "queer", "molest",
  "rape", "suck", "kill", "die", "suicide", "abuse", "drugs", "drug", "weed", "pot", "marijuana",
  "cocaine", "heroin", "meth", "lsd", "crack", "fentanyl", "nicotine", "vape", "ecig", "e-cig",
  // Leetspeak/obvious variants
  "f4g", "b1tch", "sh1t", "d1ck", "c0ck", "s3x", "pr0n", "cvm"
);

// Chinese (Mandarin) common profanity, sexual, address terms, etc.
filter.addWords(
  "屌", "幹", "幹你", "他媽", "他媽的", "媽的", "王八", "白癡", "智障", "雞巴", "陰道", "性交", "性愛", "強姦", "強暴", "死", "自殺",
  "性愛", "騷", "小三", "台女", "台男", "台客", "妓女", "亂倫", "裸照", "約炮", "吸毒", "毒品", "毒", "大麻", "安非他命", "搶劫",
  "槍", "刀", "殺", "殺人", "殺死", "砍", "砍人", "暴力", "虐待", "強奸", "強奸犯"
);

// Common address/PII patterns (if you want extra redundancy)
filter.addWords(
  "phone", "telephone", "address", "住址", "電話", "地址", "信箱", "email", "郵箱", "樓", "室", "號", "路", "街", "巷", "弄", "區", "市", "縣"
);

function containsPersonalInfo(text) {
  // US/Canada phone pattern
  if (/(?:\+?1\s*[-.]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/.test(text)) return true;
  // Long digit sequences (7+)
  if (/\b\d{7,}\b/.test(text)) return true;
  // Email
  if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(text)) return true;
  // Addresses (add more terms as needed)
  if (/\b(street|road|avenue|apt|drive|lane|city|zip|postal|block|unit|suite|台北市|高雄市|新北市|區|路|巷|弄|號|floor|building|apt)\b/i.test(text)) return true;
  return false;
}

/* ===== 1. ChatCard ===== */
function ChatCard({ chat, msgTxt, setMsgTxt, sendMessage, defaultProfile, className }) {
  const listRef = useRef(null);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [chat]);
  return (
    <div className={className ? className : ""}>
      <h3>GLOBAL CHAT</h3>
      <div className="chat-window" ref={listRef}>
        {chat.map((m, i) => (
          <div key={i} className="chat-message">
            <img
              src={m.avatar || defaultProfile}
              alt=""
              className="chat-user-thumbnail"
            />
            <strong>{m.user}:</strong> {m.text}
          </div>
        ))}
      </div>
      <div className="chat-input-area">
        <input
          className="chat-input"
          value={msgTxt}
          placeholder="Type a message…"
          onChange={e => setMsgTxt(e.target.value)}
          onKeyDown={e => e.key === "Enter" && sendMessage()}
        />
        <button className="chat-send-btn" onClick={sendMessage}>
          Send
        </button>
      </div>
    </div>
  );
}


/* ===== 2. SubjectsCard ===== */
function SubjectsCard() {
  return (
    <div className="left-panel">
      <h4 className="left-panel-title">Self-Taught Lesson</h4>
      <ul className="left-panel-list">
        <li>
          <img src={KoalaEnglish} alt="English" className="subject-thumbnail-icon" />
          <Link to="/English">English</Link>
        </li>
        <li>
          <img src={KoalaScience} alt="Science" className="subject-thumbnail-icon" />
          <Link to="/Science">Science</Link>
        </li>
        <li>
          <img src={KoalaMath} alt="Math" className="subject-thumbnail-icon" />
          <Link to="/math">Math</Link>
        </li>
        <li>
          <img src={KoalaHistory} alt="History" className="subject-thumbnail-icon" />
          <Link to="/history">History</Link>
        </li>
      </ul>
      <h4 className="left-panel-title"><Link to="/games">Games</Link></h4>
      <h4 className="left-panel-title"><Link to="/mall">Mall</Link></h4>
      <h4 className="left-panel-title">Tutor</h4>
    </div>
  );
}

/* ===============================
   MAIN COMPONENT
================================ */
export default function HomePage3Column({ user }) {
  const [chat, setChat] = useState([]);
  const [msgTxt, setMsgTxt] = useState("");
  const [isPhone, setIsPhone] = useState(window.innerWidth <= 500);

  // For assistant dual chat
  const [chatKids, setChatKids] = useState([]);
  const [chatParents, setChatParents] = useState([]);
  const [msgTxtKids, setMsgTxtKids] = useState("");
  const [msgTxtParents, setMsgTxtParents] = useState("");
  const [activeChat, setActiveChat] = useState(null);
  const [unreadMap, setUnreadMap] = useState({});

  // 新增：kids 列表與 selectedKid
  const [kids, setKids] = useState([]); // [{ uid, firstName, ... }]
  const [selectedKid, setSelectedKid] = useState(""); // 當前要顯示的 kid uid
  const [practiceRecords, setPracticeRecords] = useState([]);


  useAutoLogout(user);
  usePresence(user);
  
  const sendMessage = async () => {
  const trimmed = msgTxt.trim();
  if (!trimmed) return;
  if (!user) return alert("Please log in first!");

  if (filter.isProfane(trimmed) || containsPersonalInfo(trimmed)) {
    alert("Sorry, that message can't be sent. Please do not use inappropriate language or share personal information like phone, address, or email.");
    return;
  }

  const roomPath =
    user.accountType === "Parent"
      ? "chatRooms/globalParents/messages"
      : "chatRooms/globalKids/messages";
  const avatarUrl = user.photoURL || user.avatar || defaultProfile;
  try {
    await addDoc(collection(db, roomPath), {
      user: user.firstName,
      uid: user.uid,
      text: trimmed,
      avatar: avatarUrl,
      ts: serverTimestamp(),
    });
    setMsgTxt("");
  } catch (e) {
    console.error("send failed", e);
  }
};

const sendMessageParents = async () => {
  const trimmed = msgTxtParents.trim();
  if (!trimmed) return;
  if (!user) return alert("Please log in first!");
  if (filter.isProfane(trimmed) || containsPersonalInfo(trimmed)) {
    alert("Sorry, that message can't be sent. No inappropriate language or personal info.");
    return;
  }
  const avatarUrl = user.photoURL || user.avatar || defaultProfile;
  try {
    await addDoc(collection(db, "chatRooms/globalParents/messages"), {
      user: user.firstName,
      uid: user.uid,
      text: trimmed,
      avatar: avatarUrl,
      ts: serverTimestamp(),
    });
    setMsgTxtParents("");
  } catch (e) {
    console.error("send failed", e);
  }
};

const sendMessageKids = async () => {
  const trimmed = msgTxtKids.trim();
  if (!trimmed) return;
  if (!user) return alert("Please log in first!");
  if (filter.isProfane(trimmed) || containsPersonalInfo(trimmed)) {
    alert("Sorry, that message can't be sent. No inappropriate language or personal info.");
    return;
  }
  const avatarUrl = user.photoURL || user.avatar || defaultProfile;
  try {
    await addDoc(collection(db, "chatRooms/globalKids/messages"), {
      user: user.firstName,
      uid: user.uid,
      text: trimmed,
      avatar: avatarUrl,
      ts: serverTimestamp(),
    });
    setMsgTxtKids("");
  } catch (e) {
    console.error("send failed", e);
  }
};


   // 1️⃣ 家長抓出所有 kid（根據 parentCode 反查所有 kid）
  useEffect(() => {
    if (user?.accountType !== "Parent" || !user?.parentCode) return;
    async function fetchKids() {
      const q = query(
        collection(db, "users"),
        where("parentCode", "==", user.parentCode),
        where("accountType", "==", "Kid"),
        orderBy("firstName")
      );
      const snap = await getDocs(q);
      const arr = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
      setKids(arr);
      if (arr.length > 0) setSelectedKid(arr[0].uid);
    }
    fetchKids();
  }, [user]);

  // 2️⃣ selectedKid 改變時撈取 practiceRecords
useEffect(() => {
  if (!selectedKid) {
    setPracticeRecords([]);
    return;
  }
  async function fetchPracticeRecords() {
    const colRef = collection(db, "users", selectedKid, "practiceRecords");
    const snap = await getDocs(colRef);
    let allRecords = [];
    snap.forEach(doc => {
      const d = doc.data();
      // 每個 doc 就是一筆紀錄
      allRecords.push({
        ...d,
        subject: d.toolType || doc.id,
        date: d.timestamp || d.finishedAt || d.startedAt, // 優先用 timestamp，為了處理中文
      });
    });
    setPracticeRecords(allRecords);
  }
  fetchPracticeRecords();
}, [selectedKid]);


  // Friends management (same as before)
  const {
    kidsOnline,
    friends,
    friendsOnline,
    friendRequests,
    pendingRequests,
    sendFriendRequest,
    acceptFriendRequest,
    denyFriendRequest,
    removeFriend,
    messageFriend
  } = useFriendManager(user);

  // Global chat (same as before)
  useEffect(() => {
    if (!user?.uid || user.accountType === "Assistant") return;
    const twoHoursAgo = Timestamp.fromDate(new Date(Date.now() - 2 * 60 * 60 * 1000));
    const roomPath =
      user.accountType === "Parent"
        ? "chatRooms/globalParents/messages"
        : "chatRooms/globalKids/messages";
    const q = query(
      collection(db, roomPath),
      orderBy("ts", "asc"),
      where("ts", ">=", twoHoursAgo),
      limit(200)
    );
    const unsub = onSnapshot(q, s => setChat(s.docs.map(d => d.data())));
    return () => unsub();
  }, [user?.uid, user?.accountType]);

  // Assistant chat channels
  useEffect(() => {
    if (!user?.uid || user.accountType !== "Assistant") return;
    const ts = Timestamp.now();
    const kidsQ = query(
      collection(db, "chatRooms/globalKids/messages"),
      orderBy("ts", "asc"),
      where("ts", ">=", ts),
      limit(200)
    );
    const parentsQ = query(
      collection(db, "chatRooms/globalParents/messages"),
      orderBy("ts", "asc"),
      where("ts", ">=", ts),
      limit(200)
    );
    const unsubKids = onSnapshot(kidsQ, s => setChatKids(s.docs.map(d => d.data())));
    const unsubParents = onSnapshot(parentsQ, s => setChatParents(s.docs.map(d => d.data())));
    return () => { unsubKids(); unsubParents(); };
  }, [user?.uid, user?.accountType]);

  // Friends unread notifications (same as before)
  useEffect(() => {
    if (!user?.uid || !friends || friends.length === 0) return;
    const unsubs = [];
    friends.forEach(friend => {
      const chatId = [user.uid, friend.uid].sort().join("_");
      const q = query(
        collection(db, "privateChats", chatId, "messages"),
        where("to", "==", user.uid)
      );
      const unsub = onSnapshot(q, snap => {
        setUnreadMap(prev => ({
          ...prev,
          [friend.uid]: !snap.empty
        }));
      });
      unsubs.push(unsub);
    });
    return () => unsubs.forEach(unsub => unsub());
  }, [user?.uid, friends]);

  // Window resize for mobile detection (same as before)
  useEffect(() => {
    const handleResize = () => setIsPhone(window.innerWidth <= 500);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // === Conditional Returns ===

  // 1. Assistant layout
  if (user?.accountType === "Assistant") {
    return (
      <div className="three-column-layout">
        <SubjectsCard />
        <div className="assistant-chat-channels">
          <div className="assistant-chat-panel">
            <h3 className="assistant-chat-label">Parents Channel</h3>
            <ChatCard
              chat={chatParents}
              msgTxt={msgTxtParents}
              setMsgTxt={setMsgTxtParents}
              sendMessage={sendMessageParents}
              defaultProfile={defaultProfile}
            />
          </div>
          <div className="assistant-chat-panel">
            <h3 className="assistant-chat-label">Kids Channel</h3>
            <ChatCard
              chat={chatKids}
              msgTxt={msgTxtKids}
              setMsgTxt={setMsgTxtKids}
              sendMessage={sendMessageKids}
              defaultProfile={defaultProfile}
            />
          </div>
        </div>
        <FriendsCard
          kidsOnline={kidsOnline}
          friendsOnline={friendsOnline}
          myFriends={friends}
          friendRequests={friendRequests}
          pendingRequests={pendingRequests}
          onSendRequest={sendFriendRequest}
          onAcceptRequest={acceptFriendRequest}
          onDenyRequest={denyFriendRequest}
          onRemoveFriend={removeFriend}
          onMessageFriend={f => setActiveChat(f)}
          currentUser={user}
          unreadMap={unreadMap}
        />
      </div>
    );
  }

  // 2. Parent: Mobile layout
  if (isPhone && user?.accountType === "Parent") {
    return (
      <div className="three-column-layout phone-layout">
        <ChatCard
          className="center-chat-panel"
          chat={chat}
          msgTxt={msgTxt}
          setMsgTxt={setMsgTxt}
          sendMessage={sendMessage}
          defaultProfile={defaultProfile}
        />
        <div className="merge-card">
          {kids.length > 1 && (
            <div style={{ marginBottom: 10 }}>
              <select value={selectedKid} onChange={e => setSelectedKid(e.target.value)}>
                {kids.map(k => (
                  <option key={k.uid} value={k.uid}>
                    {k.firstName} {k.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <ParentInsightsPanel records={practiceRecords} />
        {activeChat && (
          <PrivateChatCard
            me={user}
            friend={activeChat}
            onClose={() => setActiveChat(null)}
          />
        )}
      </div>
    );
  }

  // 3. Parent: Desktop layout
  if (!isPhone && user?.accountType === "Parent") {
    return (
      <div className="three-column-layout">
        <SubjectsCard />
        <ChatCard
          className="center-chat-panel"
          chat={chat}
          msgTxt={msgTxt}
          setMsgTxt={setMsgTxt}
          sendMessage={sendMessage}
          defaultProfile={defaultProfile}
        />
        <div style={{ width: "100%" }}>
          {kids.length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <label>
                <b>Switch Kid:</b>{" "}
                <select
                  value={selectedKid}
                  onChange={e => setSelectedKid(e.target.value)}
                  style={{ padding: 6, fontSize: 15 }}
                >
                  {kids.map(k => (
                    <option key={k.uid} value={k.uid}>
                      {k.firstName} {k.lastName} ({k.accountName})
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <ParentInsightsPanel records={practiceRecords} />
        </div>
      </div>
    );
  }

  // 4. Default: Kids (desktop/mobile)
return (
  <div className="three-column-layout phone-layout">
    <SubjectsCard />
    <ChatCard
      className="center-chat-panel"
      chat={chat}
      msgTxt={msgTxt}
      setMsgTxt={setMsgTxt}
      sendMessage={sendMessage}
      defaultProfile={defaultProfile}
    />
    <div className="merge-card"></div>
    <FriendsCard
      kidsOnline={kidsOnline}
      friendsOnline={friendsOnline}
      myFriends={friends}
      friendRequests={friendRequests}
      pendingRequests={pendingRequests}
      defaultProfile={defaultProfile}
      onSendRequest={sendFriendRequest}
      onAcceptRequest={acceptFriendRequest}
      onDenyRequest={denyFriendRequest}
      onRemoveFriend={removeFriend}
      onMessageFriend={f => setActiveChat(f)}
      currentUser={user}
      unreadMap={unreadMap}
    />
    {activeChat && (
      <PrivateChatCard
        me={user}
        friend={activeChat}
        onClose={() => setActiveChat(null)}
      />
    )}
  </div>
);
}